import type { Funnel } from "schemas/generated/funnel";

import React from "react";

import { useModal } from "@editor/hooks/useModal";

import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { EyeOff, Pencil, Settings2, Trash2 } from "lucide-react";

const FunnelSettingsMenu = ({
  funnel,
  setIsRenamingFunnel,
}: {
  funnel: Funnel;
  setIsRenamingFunnel: (isRenaming: boolean) => void;
}) => {
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = React.useState(false);
  const { openModal } = useModal();

  const publishedPagesCount = funnel.funnelSteps.reduce((acc, step) => {
    const file = step.file;
    if (!file || !file.lastPublishedFileVersionId) {
      return acc;
    }
    return acc + 1;
  }, 0);

  return (
    <Combobox.Root
      open={isSettingsMenuOpen}
      onOpenChange={setIsSettingsMenuOpen}
      onChange={(value: string) => {
        if (value === "unpublish") {
          openModal({
            type: "unpublishModal",
            props: { type: "funnel", funnel },
          });
        } else if (value === "delete") {
          openModal({
            type: "deleteFunnelModal",
            props: { funnel },
          });
        } else if (value === "rename") {
          setIsRenamingFunnel(true);
        }
        setIsSettingsMenuOpen(false);
      }}
      options={[
        {
          value: "rename",
          label: (
            <MenuItem variant="default" startEnhancer={<Pencil size={12} />}>
              Rename Funnel
            </MenuItem>
          ),
          displayValue: "Rename",
        },
        {
          value: "unpublish",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<EyeOff size={12} />}
              disabled={publishedPagesCount === 0}
              toolTipText={
                publishedPagesCount === 0
                  ? "Funnel must have a published page to unpublish"
                  : undefined
              }
            >
              {`Unpublish Pages${publishedPagesCount > 0 ? ` (${publishedPagesCount})` : ""}`}
            </MenuItem>
          ),
          displayValue: "Unpublish",
        },
        {
          value: "delete",
          label: (
            <MenuItem variant="default" startEnhancer={<Trash2 size={12} />}>
              Delete Funnel
            </MenuItem>
          ),
          displayValue: "Delete",
        },
      ]}
    >
      <Combobox.Trigger>
        <IconButton
          variant="secondary"
          size="base"
          icon={<Settings2 size={16} />}
        />
      </Combobox.Trigger>
      <Combobox.Popover layoutClassName="w-48">
        <Combobox.Content />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

export default FunnelSettingsMenu;
